import React, { ReactNode, useEffect, useState } from 'react';
import styled from 'styled-components';

import {
  ARTICLE_TYPES,
  BUTTON_TYPES,
  ICON_TYPES,
} from '../../../shared/constants';
import { IArticle, Tag as ITag } from '../../../types/shared';

import ArticlePreviewsWrapper from '../../atoms/article-previews-wrapper/ArticlePreviewsWrapper';
import { Chip, ChipList, ChipListItem } from '../../atoms/chip/Chip';
import SecondaryButton from '../../atoms/secondary-button/SecondaryButton';

import { DEQ_COMPS } from '../../../utils/deq';
import { trackTag } from '../../../utils/deq/trackTag';
import { getInternalLink } from '../../../utils/generic';
import { BUTTON_LABELS, languageHelper } from '../../../utils/language';
import { LABELS } from '../../../utils/language/filter-labels';
import { getTagsByTypes } from '../../../utils/tags';

import mq from '../../../styles/breakpoints';
import Colors from '../../../styles/colors';
import Spacings from '../../../styles/Spacings';

import { TagTypes } from '../../../tokens';
import ArticlePreviewCard from '../article-preview-card/ArticlePreviewCard';

interface Props {
  articlesArray: IArticle[];
  children: ReactNode | ReactNode[];
  tags: ITag[];
}

const FilterableArticlePreviews = ({
  articlesArray,
  children,
  tags,
}: Props) => {
  const filterableTags =
    tags?.length > 0
      ? [{ name: 'Alle verhalen', value: 'alle-verhalen' }, ...tags]
      : undefined;
  const [tagSelected, setTagSelected] = useState({
    name: 'Alle verhalen',
    value: 'alle-verhalen',
  });

  const [articles, setArticles] = useState<IArticle[]>([]);
  const { lang } = languageHelper();

  // TODO: There was an bug where only in Firefox the correct order appeared in the Article Preview Container.
  // This was because in Firefox .sort() works the other way around 'a - b' instead of 'b - a' for all other browsers.
  const sortArticlesBasedOnBrowser = (
    articleOne: IArticle,
    articleTwo: IArticle
  ) => {
    const userAgentString = navigator.userAgent;
    const firefoxAgent = userAgentString.indexOf('Firefox') > -1;

    if (firefoxAgent) {
      return (
        new Date(articleOne.publishingTime).valueOf() -
        new Date(articleTwo.publishingTime).valueOf()
      );
    }
    return (
      new Date(articleTwo.publishingTime).valueOf() -
      new Date(articleOne.publishingTime).valueOf()
    );
  };

  useEffect(() => {
    if (articlesArray?.length) {
      setArticles(
        articlesArray
          .sort((articleOne, articleTwo) => {
            return sortArticlesBasedOnBrowser(articleOne, articleTwo);
          })
          .sort((article) => {
            if (
              article.tags !== null &&
              article?.tags?.filter((tag) => (tag as ITag)?.name === 'Featured')
                .length
            ) {
              return -1;
            }
            return 1;
          })
      );
    }
  }, [articlesArray]);

  const handleTagSelection = (name: string, value: string) => {
    const tagValue = value.toLowerCase();
    const filteredArticles =
      tagValue === 'alle-verhalen'
        ? articlesArray
        : articlesArray
            ?.filter((article) => {
              return article.tags?.some(
                (tag) => (tag as ITag)?.value?.toLowerCase() === tagValue
              );
            })
            .sort((articleOne, articleTwo) => {
              return sortArticlesBasedOnBrowser(articleOne, articleTwo);
            })
            .sort((article) => {
              return article.tags?.length &&
                article?.tags?.filter(
                  (tag) => (tag as ITag)?.name === 'Featured'
                ).length
                ? -1
                : 1;
            });

    setArticles(filteredArticles);
    setTagSelected({ name, value: tagValue });
    trackTag({ name, value: tagValue }, DEQ_COMPS.articlePreviewTag);
  };

  const threeLatestArticles = articles?.slice(0, 3);
  const contentOverviewPage = articlesArray?.length
    ? articlesArray[0].parentPage
    : '/magazine';

  return (
    <Container id="filterableArticlePreviews">
      {tags && children && <Title data-testid="title">{children}</Title>}
      <Filters>
        {!tags && children && <Title data-testid="title">{children}</Title>}
        {tags && filterableTags?.length && (
          <TagsContainer>
            <StyledChipList>
              {filterableTags?.map((tag, index) => (
                <ChipListItem key={index}>
                  <Chip
                    label={tag.name}
                    value={tag.value}
                    isActive={tagSelected.value === tag.value}
                    onClick={() => handleTagSelection(tag.name, tag.value)}
                  />
                </ChipListItem>
              ))}
            </StyledChipList>
          </TagsContainer>
        )}
        <StyledSecondaryButton
          to={`/${getInternalLink(contentOverviewPage)}`}
          label={BUTTON_LABELS.viewArticles[lang]}
          type={BUTTON_TYPES.internal}
          icon={ICON_TYPES.chevron}
        />
      </Filters>
      {threeLatestArticles?.length ? (
        <FogContainer>
          <ArticlePreviewsWrapper>
            {threeLatestArticles.map((article, index) => (
              <ArticlePreviewCard
                image={article.mainImage?.gatsbyImageData}
                contentType={article.articleType && article?.articleType}
                tags={getTagsByTypes(article.tags || [], [
                  TagTypes.SECTOR,
                  TagTypes.CATEGORY,
                ])}
                title={article.titleForCarousel && article?.titleForCarousel}
                data={article}
                articleType={ARTICLE_TYPES.internal}
                cardType={article?.previewCardType}
                key={`content-preview-card-${index}`}
              />
            ))}
          </ArticlePreviewsWrapper>
        </FogContainer>
      ) : (
        <ArticlePreviewsWrapper>
          <NoArticles>{LABELS.noItems[lang]}</NoArticles>
        </ArticlePreviewsWrapper>
      )}
    </Container>
  );
};

export default FilterableArticlePreviews;

const Container = styled.section`
  width: 100%;
`;

const FogContainer = styled.div`
  position: relative;
`;

const Title = styled.div`
  box-sizing: border-box;
  font-size: 24px;
  font-style: normal;
  font-weight: normal;
  line-height: 32px;
  margin: 0 0 ${Spacings.XS};
  width: 100%;

  ${mq.from.breakpoint.M`
     margin: 0 0 ${Spacings.S};
  `}

  ${mq.from.breakpoint.L`
    font-size: 32px;
    line-height: 40px;
    margin-bottom: ${Spacings.M};
  `}
`;

const TagsContainer = styled.div`
  width: 100%;

  ${mq.from.breakpoint.M`
    width: auto;
  `}

  ${mq.from.breakpoint.L`
    display: flex;
    flex-direction: row;
    align-items: center;
  `}
`;

const StyledChipList = styled(ChipList)`
  justify-content: flex-start;

  ${mq.from.breakpoint.M`    
    justify-content: center;
  `}
`;

const Filters = styled.div`
  align-items: center;
  box-sizing: border-box;
  display: flex;
  flex-direction: column-reverse;
  flex-wrap: wrap;
  justify-content: space-between;
  margin: 0 auto ${Spacings.M};
  padding: 0;
  width: 100%;

  ${mq.from.breakpoint.M`
    flex-direction: row;
    margin: 0 auto ${Spacings.S};
  `}

  ${mq.from.breakpoint.L`
    flex-direction: row;
    margin: 0 auto ${Spacings.M};
  `}
`;

const NoArticles = styled.div`
  color: ${Colors.abnBlack};
  font-family: ${({ theme }) => theme.fontRegular};
  font-size: 16px;
  font-style: normal;
  font-weight: normal;
  line-height: 24px;
  padding: 56px 0;
  text-align: center;

  ${mq.from.breakpoint.M`
    padding: 134px 0 142px;
    font-size: 24px;
    line-height: 32px;
  `}
`;

const StyledSecondaryButton = styled(SecondaryButton)`
  margin-bottom: ${Spacings.S};
  min-width: 100%;

  ${mq.from.breakpoint.M`
    min-width: 100px;
    margin-bottom: 0;
  `}
`;
